import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Flagship module
 */
export default class Flagship extends MessageBus {
  /**
   * Constructor function of the Flagship class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();

    this.element = element;
    this.visibleTooltip = null; // Stores the currently open tooltip

    // Initialize the module
    this.init();
  }

  /**
   * Initializes the module by calling the functions to
   * create DOM references, setup event handlers, and enable interactions.
   * @returns {Flagship} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    window.addEventListener('resize', this.handleResize.bind(this));

    this.setupScrollAnimations();

    return this;
  }

  /**
   * Cache DOM References
   * @returns {Flagship} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.flagshipWraps = this.element.querySelectorAll(
      `.${CLASS_NAMES.FLAGSHIP_WRAP_JS}`
    );
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {Flagship} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleTooltipToggle = this.handleTooltipToggle.bind(this);
    this.closeTooltip = this.closeTooltip.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    return this;
  }

  /**
   * Enable interactions by applying custom tooltip logic.
   * @returns {Flagship} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (!this.flagshipWraps.length) return this;

    if (window.innerWidth >= 1024) {
      this.flagshipWraps.forEach((wrap) => {
        wrap.addEventListener(EVENTS.CLICK, this.handleTooltipToggle);
      });
    }

    this.closeButtons = this.element.querySelectorAll(
      `.${CLASS_NAMES.FLAGSHIP_CLOSE_JS}`
    );
    this.closeButtons.forEach((btn) => {
      btn.addEventListener(EVENTS.CLICK, (event) => {
        event.stopPropagation(); // Prevents event bubbling issues
        this.closeTooltip();
      });
    });

    return this;
  }

  /**
   * Handles tooltip toggle functionality.
   * @param {Event} event - Click event
   */
  handleTooltipToggle(event) {
    event.stopPropagation();

    const wrap = event.currentTarget;
    const iso = wrap.getAttribute(`${SELECTORS.DATA_ISO}`);
    const tooltipDiv = document.querySelector(`#${SELECTORS.TOOLTIP}-${iso}`);

    if (!tooltipDiv) return;

    // Close any open tooltip before opening a new one
    if (this.visibleTooltip && this.visibleTooltip !== tooltipDiv) {
      this.closeTooltip();
    }

    // Toggle visibility
    const isVisible = tooltipDiv.classList.contains(`${CLASS_NAMES.VISIBLE}`);

    if (!isVisible) {
      this.closeTooltip();

      wrap.classList.add(`${CLASS_NAMES.SELECTED}`);
      tooltipDiv.classList.add(`${CLASS_NAMES.VISIBLE}`);
      this.visibleTooltip = tooltipDiv;

      // Close tooltip when clicking outside
      document.addEventListener(EVENTS.CLICK, this.handleOutsideClick);
    } else {
      this.closeTooltip();
    }

    // Prevent tooltip from closing when clicking inside
    tooltipDiv.addEventListener(EVENTS.CLICK, (e) => e.stopPropagation());
  }

  /**
   * Closes the currently open tooltip when clicked outside of the popup
   * @param {Event} event - Click event
   */
  handleOutsideClick(event) {
    if (this.visibleTooltip && !this.visibleTooltip.contains(event.target)) {
      this.closeTooltip();
    }
  }

  /**
   * Closes the currently open tooltip.
   */
  closeTooltip() {
    if (!this.visibleTooltip) return;

    this.visibleTooltip.classList.remove(`${CLASS_NAMES.VISIBLE}`);

    document
      .querySelectorAll(
        `.${CLASS_NAMES.FLAGSHIP_WRAP_JS}.${CLASS_NAMES.SELECTED}`
      )
      .forEach((wrap) => wrap.classList.remove(`${CLASS_NAMES.SELECTED}`));

    document.removeEventListener(EVENTS.CLICK, this.handleOutsideClick);

    this.visibleTooltip = null;
  }

  handleResize() {
    if (window.innerWidth < 1024) {
      // Disable click events on small screens
      this.flagshipWraps.forEach((wrap) => {
        wrap.removeEventListener(EVENTS.CLICK, this.handleTooltipToggle);
      });
    } else {
      // Re-enable click events if screen size is >= 1024px
      this.enable();
    }
  }

  setupScrollAnimations() {
    this.handleScroll(); // Run once on page load
    window.addEventListener(EVENTS.SCROLL, this.handleScroll.bind(this));
  }

  handleScroll() {
    this.flagshipWraps.forEach((wrap, index) => {
      const rect = wrap.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      if (rect.top < windowHeight * 0.8) {
        setTimeout(() => {
          wrap.classList.add(CLASS_NAMES.ANIMATED);
        }, 150 + index * 150);
      } else if (rect.bottom < 0 || rect.top > windowHeight) {
        wrap.classList.remove(CLASS_NAMES.ANIMATED);
      }
    });
  }
}
